import { useCallback, useEffect, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
  FormControl,
  Switch,
} from '@chakra-ui/react';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { TextareaField } from '@/components/TextareaField';
import { ImageUpload } from '@/components/ImageUpload';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePutNewsMutation } from '@/services/queries/news';
import { News, NewsStatus } from '@/model';
import { PutNewsCalendarNewsRequest } from '@/services/request/news';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface NewsEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: News;
  onClose: () => void;
  onDone?: () => void;
}

export const NewsEditModal = ({ isOpen, onClose, item, onDone }: NewsEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutNewsMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid ?? '',
      title: item.title,
      file: undefined,
      content: item.content,
      source_url: item.source_url,
      publish_at: item.publish_at ?? '',
      is_published: item.status === NewsStatus.PUBLISHED,
      is_scheduled: item.status === NewsStatus.SCHEDULED,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const onInvalid = useCallback(
    (formErrors: FieldErrors<ModalFormData>) => {
      const firstError = Object.values(formErrors)[0] as { message?: string } | undefined;
      toast({
        status: 'error',
        title: firstError?.message || t('pages.application_news.components.news_edit_modal.news_edit_error'),
      });
    },
    [toast, t],
  );

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      const payload: PutNewsCalendarNewsRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.file,
        title: data.title,
        content: data.content,
        source_url: data.source_url,
        is_published: data.is_scheduled && data.publish_at ? false : data.is_published,
        publish_at: data.is_scheduled ? data.publish_at : '',
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_news.components.news_edit_modal.news_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_news.components.news_edit_modal.news_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  const handleConfirm = useCallback(() => {
    handleSubmit(onSubmit, onInvalid)();
  }, [handleSubmit, onSubmit, onInvalid]);

  useEffect(() => {
    if (isOpen && item?.team_uuid) {
      setValue('team_uuid', item.team_uuid);
    }
  }, [isOpen, item?.team_uuid, setValue]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_news.components.news_edit_modal.news_edit_title')}
            </Text>
            <FormControl w='fit-content' display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium' fontSize='14px' color={!formValues.is_published ? 'gray.400' : 'inherit'}>
                {t('pages.application_news.components.news_edit_modal.news_edit_published')}
              </Text>
              <Switch
                disabled={!!formValues.is_scheduled}
                name='is_published'
                isChecked={formValues.is_scheduled ? false : formValues.is_published}
                onChange={event => setValue('is_published', event.target.checked)}
              />
            </FormControl>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit, onInvalid)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_news.components.news_create_modal.news_create_label_team')}
              placeholder={t('pages.application_news.components.news_create_modal.news_create_label_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_news.components.news_edit_modal.news_edit_label')}
              {...register('title')}
              helperText={t('pages.application_news.components.news_edit_modal.news_edit_helper_text')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.content}>
              <Text fontWeight='medium'>{t('pages.application_news.components.news_edit_modal.news_edit_label_text')}</Text>

              <TextareaField
                maxLength={4000}
                helperText={t('pages.application_news.components.news_edit_modal.news_edit_helper_text_label')}
                error={errors?.content?.message}
                {...register('content')}
              />
            </FormControl>

            <InputField
              withoutFloating
              label={t('pages.application_news.components.news_edit_modal.news_edit_label_url')}
              {...register('source_url')}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_news.components.news_edit_modal.news_edit_label_image')}
                imageURL={item.image_url ?? ''}
                onFileChange={file => {
                  setValue('file', file);
                }}
                helpText={t('pages.application_news.components.news_edit_modal.news_edit_help_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>

            {formValues.is_scheduled && (
              <InputField
                withoutFloating
                label={t('pages.application_news.components.news_edit_modal.news_edit_date_label')}
                type='datetime-local'
                fullDatePickerArea
                min={formatDateToDateTimeLocalInputValue(new Date())}
                {...register('publish_at')}
                rootProps={{ isInvalid: !!errors?.publish_at, errorMessage: errors?.publish_at?.message }}
              />
            )}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_news.components.news_edit_modal.news_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>{t('pages.application_news.components.news_edit_modal.news_edit_save')}</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
